import log from 'loglevel';

import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tenant } from 'state/tenant';
import { setStoreLocations, selectStoreLocations } from 'state/storeLocations';
import { Status } from 'hooks/requests/fetchUtils';
import useFetch from 'hooks/useFetch';

export interface StoreLocation {
  lat: number;
  long: number;
  storeNumber: string;
}

export class StoreLocationsError extends Error {}

type StoreLocationHook = () => [
  (tenant: Tenant) => Promise<StoreLocation[]>,
  Status,
];

const useStoreLocations: StoreLocationHook = () => {
  const [status, fetch] = useFetch();
  const dispatch = useDispatch();
  const storeLocations = useSelector(selectStoreLocations);

  const fetchStoreLocations = useCallback(
    async (tenant: Tenant): Promise<StoreLocation[]> => {
      if (storeLocations) {
        return Promise.resolve(storeLocations);
      }

      if (!tenant.geolocationAttribution) {
        throw new Error('tenant does not support geolocationAttribution');
      }

      const url = tenant.geolocationAttribution.storeLocationsUrl;
      const response = await fetch(url, {
        method: 'GET',
        mode: undefined,
        headers: undefined,
      });

      if (!response.ok) {
        log.error('Failed to fetch store locations');
        return Promise.reject(
          new StoreLocationsError('Failed to fetch store locations'),
        );
      }

      const asyncStoreLocations = (response.json() as unknown) as Promise<
        StoreLocation[]
      >;

      return asyncStoreLocations.then(storeLocations => {
        dispatch(setStoreLocations(storeLocations));
        return storeLocations;
      });
    },
    [dispatch, fetch, storeLocations],
  );

  return [fetchStoreLocations, status];
};
export default useStoreLocations;
